import { useEffect } from "react";
import { LuTriangleAlert, LuX } from "react-icons/lu";
import { Button } from "./button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Reusable confirm dialog used for destructive actions in the console.
 * Backdrop click + Escape both cancel; the confirm button is the
 * primary action and gets focus when the dialog opens.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  pending = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !pending) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pending, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={() => !pending && onClose()}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-md rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3 p-5 border-b border-[var(--color-border)]">
          <div
            className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
              destructive
                ? "bg-[rgba(237,66,69,0.1)] border border-[rgba(237,66,69,0.25)] text-[var(--color-error)]"
                : "bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.25)] text-[var(--color-accent-light)]"
            }`}
          >
            <LuTriangleAlert className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-[var(--color-text)]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="text-[var(--color-text-subtle)] hover:text-[var(--color-text)] transition-colors p-1 -mr-1 -mt-1 disabled:opacity-50"
            title="Close"
          >
            <LuX className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 text-sm text-[var(--color-text-muted)] leading-relaxed">
          {description}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 pt-0">
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={pending}
            autoFocus
          >
            {pending ? "Working..." : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
